import { useTranslation } from 'react-i18next';
import { getPublicStandings } from '@/lib/api';
import { usePolledData } from '@/lib/usePolledData';
import { ApiError } from '@/lib/api/core';
import { useAuth } from '@/contexts/AuthContext';
import { StandingsTable } from '@/components/standings/StandingsTable';
import type { StandingsResponse } from '@swiss-magic/shared-types';
import { useHubOutletContext } from './TournamentHubLayoutRoute';

// Story 083 — director-facing Standings inside the hub. Reuses the
// public standings data (it's the same numbers the spectators see) and
// the shared StandingsTable. Once the tournament is completed the
// rank-1 row is flagged as the winner so the director sees the result
// without leaving the hub.

export function TournamentStandingsPage() {
  const { t } = useTranslation('tournament');
  const { t: td } = useTranslation('director');
  const { tournament } = useHubOutletContext();
  const { user } = useAuth();
  const slug = tournament?.slug;

  const { data, message, loading } = usePolledData<StandingsResponse>(
    () => getPublicStandings(slug as string),
    [slug],
    {
      // Pre-round-1 the endpoint 404s with the standings_unavailable
      // code; surface the friendly "not yet" copy instead.
      errorMapper: (_msg, error) =>
        error instanceof ApiError &&
        error.code === 'validation.standings.standings_unavailable'
          ? t('standings.not_yet')
          : null,
    },
  );

  if (!tournament) {
    return (
      <div className="text-sm text-muted-foreground">
        {td('tournament_hub.loading')}
      </div>
    );
  }

  const isCompleted = tournament.status === 'completed';

  return (
    <div data-testid="tournament-hub-standings-page" className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold md:text-2xl">
          {td('tournament_hub.standings.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('standings.subtitle')}
        </p>
      </div>

      {loading && !data && (
        <p className="text-sm text-muted-foreground">{t('loading')}</p>
      )}

      {message && (
        <p
          data-testid="standings-message"
          className="text-center text-muted-foreground"
        >
          {message}
        </p>
      )}

      {data && (
        <StandingsTable
          data={data}
          viewerUserId={user?.id}
          highlightWinner={isCompleted}
        />
      )}
    </div>
  );
}
